/**
 * Storage adapter — bridges the legacy synchronous persistence API to the
 * new @skjv/storage router. Writes fan out to all configured providers;
 * reads come from the local provider envelopes synchronously so the existing
 * synchronous state-loading paths keep working.
 *
 * When PR 3 adds Google Drive, this file's sync read path will be supplemented
 * by an async background reconciler that updates in-memory state from cloud.
 */

import {
  getStorageRouter,
  listLocalRefs,
  migrateLegacyLocalStorage,
  readLocalRecord,
  type Namespace,
  type StorageRecord,
} from '@skjv/storage';
import type { Study, StudyGroup } from '@skjv/common';
import { studyFileBaseName, studyFolderName, studyGroupFileBaseName } from './study-file-name';
import { studyMarkdownSidecar } from './study-sidecars';

// Eagerly migrate legacy localStorage keys so synchronous loads see the new
// envelope layout on first boot. Idempotent (flag-guarded inside the migration).
migrateLegacyLocalStorage();

const now = (): string => new Date().toISOString();

// ── Singleton helpers (one record per namespace, key='all') ───────

function loadSingleton<T>(ns: Namespace, key: string = 'all'): T | null {
  const rec = readLocalRecord<T>(ns, key);
  if (!rec || rec.deletedAt) return null;
  return rec.value ?? null;
}

function saveSingleton<T>(ns: Namespace, value: T, key: string = 'all'): void {
  const router = getStorageRouter();
  void router.putRecord<T>({
    namespace: ns,
    key,
    value,
    updatedAt: now(),
  });
}

// ── Per-item collection helpers (one record per item.id) ──────────

interface WithId {
  id: string;
  updatedAt?: string;
}

function loadCollection<T extends WithId>(ns: Namespace): T[] {
  const items: T[] = [];
  for (const ref of listLocalRefs(ns)) {
    if (ref.deletedAt) continue;
    const rec = readLocalRecord<T>(ns, ref.key);
    if (!rec || rec.deletedAt) continue;
    items.push(rec.value as T);
  }
  return items;
}

// saveCollection was retired in favour of namespace-specific writers below
// (writeStudies / writeStudyGroups) once per-record displayName + recordFileName
// + sidecar PDFs became part of the per-item save contract.

// ── Preferences ───────────────────────────────────────────────────

export interface PersistedPrefs {
  themeMode?: string;
  fontScale?: number;
  colorTheme?: string;
  isDarkMode?: boolean;
  showPericopeHeadings?: boolean;
  isReadingMode?: boolean;
}

export function savePreferences(prefs: {
  themeMode: string;
  fontScale: number;
  colorTheme: string;
  showPericopeHeadings?: boolean;
  isReadingMode?: boolean;
}): void {
  saveSingleton('prefs', prefs, 'all');
}

export function loadPreferences(): PersistedPrefs | null {
  return loadSingleton<PersistedPrefs>('prefs', 'all');
}

// ── Bookmarks ─────────────────────────────────────────────────────

export function saveBookmarks<T>(bookmarks: T[]): void {
  saveSingleton('bookmarks', bookmarks);
}
export function loadBookmarks<T>(): T[] {
  return loadSingleton<T[]>('bookmarks') ?? [];
}

// ── Notes ─────────────────────────────────────────────────────────

export function saveNotes<T>(notes: T[]): void {
  saveSingleton('notes', notes);
}
export function loadNotes<T>(): T[] {
  return loadSingleton<T[]>('notes') ?? [];
}

// ── Prayer ────────────────────────────────────────────────────────

export function savePrayerItems<T>(items: T[]): void {
  saveSingleton('prayer', items);
}
export function loadPrayerItems<T>(): T[] {
  return loadSingleton<T[]>('prayer') ?? [];
}

// ── History ───────────────────────────────────────────────────────

export function saveHistory<T>(history: T[]): void {
  saveSingleton('history', history);
}
export function loadHistory<T>(): T[] {
  return loadSingleton<T[]>('history') ?? [];
}

// ── Memory Verse ──────────────────────────────────────────────────

export interface PersistedMemoryVerse {
  verse: unknown;
  frequency: number;
  lastMillis: number | null;
}

export function saveMemoryVerse(data: PersistedMemoryVerse): void {
  saveSingleton('memory', data);
}
export function loadMemoryVerse(): PersistedMemoryVerse | null {
  return loadSingleton<PersistedMemoryVerse>('memory');
}

// ── Studies ───────────────────────────────────────────────────────

export function saveStudies(studies: Study[]): void {
  void writeStudies(studies);
}
export function loadStudies<T extends WithId>(): T[] {
  return loadCollection<T>('studies');
}

export function saveStudyGroups(groups: StudyGroup[]): void {
  writeStudyGroups(groups);
}
export function loadStudyGroups<T extends WithId>(): T[] {
  return loadCollection<T>('study-groups');
}

/**
 * Two-phase study write: JSON first (fast, hits localStorage immediately + Drive without PDF),
 * then PDF generated in the background and re-uploaded as a sidecar for Drive.
 */
async function writeStudies(studies: Study[]): Promise<void> {
  const router = getStorageRouter();
  const ts = now();
  const newIds = new Set(studies.map((s) => s.id));

  // Saves are scoped to what actually changed. This function used to
  // rewrite the WHOLE library — every record through the provider plus a
  // fresh PDF for every study — so each editor auto-save (500ms after any
  // keystroke) rewrote all 537 studies and pegged the desktop shell
  // (perf hunt 2026-09-02). A study is written when its updatedAt differs
  // from the stored record's; CRUD bumps updatedAt on every mutation.
  const existingUpdatedAt = new Map<string, string>();
  for (const ref of listLocalRefs('studies')) {
    if (ref.deletedAt) continue;
    if (!newIds.has(ref.key)) void router.deleteRecord('studies', ref.key);
    else existingUpdatedAt.set(ref.key, ref.updatedAt);
  }
  const changed = studies.filter((s) => existingUpdatedAt.get(s.id) !== (s.updatedAt ?? ''));

  // Pass 1 — JSON + the cheap markdown twin.
  for (const study of changed) {
    const markdown = studyMarkdownSidecar(study);
    void router.putRecord<Study>({
      namespace: 'studies',
      key: study.id,
      value: study,
      updatedAt: study.updatedAt ?? ts,
      displayName: studyFolderName(study),
      recordFileName: `${studyFileBaseName(study)}.json`,
      sidecars: markdown ? [markdown] : undefined,
    });
  }

  // Pass 2 — render PDFs in background and re-upload with sidecar. Lazy-import
  // so jspdf isn't pulled into the initial bundle. Changed studies only —
  // regenerating every PDF on every save was the heaviest part of the
  // full-library rewrite this function used to do.
  try {
    const { generateStudyPdf } = await import('@/services/study-pdf');
    for (const study of changed) {
      try {
        const pdf = await generateStudyPdf(study);
        const markdown = studyMarkdownSidecar(study);
        void router.putRecord<Study>({
          namespace: 'studies',
          key: study.id,
          value: study,
          updatedAt: study.updatedAt ?? ts,
          displayName: studyFolderName(study),
          recordFileName: `${studyFileBaseName(study)}.json`,
          sidecars: [
            ...(markdown ? [markdown] : []),
            {
              name: `${studyFileBaseName(study)}.pdf`,
              blob: pdf,
              contentType: 'application/pdf',
            },
          ],
        });
      } catch (err) {
        console.warn('PDF generation failed for study', study.id, err);
      }
    }
  } catch (err) {
    console.warn('Could not load study-pdf module', err);
  }
}

/**
 * Idempotent additive writes — used by bulk-import flows. Skips PDF generation
 * (PDFs are derived data; the bulk importer trades it for speed and can
 * regenerate later by editing each study).
 *
 * Does NOT tombstone non-listed records (additive only). Existing local studies
 * not present in `studies` stay where they are.
 */
export async function bulkSaveStudies(studies: Study[]): Promise<void> {
  const router = getStorageRouter();
  const ts = now();
  await Promise.all(
    studies.map((study) => {
      const markdown = studyMarkdownSidecar(study);
      return router.putRecord<Study>({
        namespace: 'studies',
        key: study.id,
        value: study,
        updatedAt: study.updatedAt ?? ts,
        displayName: studyFolderName(study),
        recordFileName: `${studyFileBaseName(study)}.json`,
        sidecars: markdown ? [markdown] : undefined,
      });
    }),
  );
}

export async function bulkSaveStudyGroups(groups: StudyGroup[]): Promise<void> {
  const router = getStorageRouter();
  const ts = now();
  await Promise.all(
    groups.map((group) =>
      router.putRecord<StudyGroup>({
        namespace: 'study-groups',
        key: group.id,
        value: group,
        updatedAt: group.updatedAt ?? ts,
        displayName: group.name,
        recordFileName: `${studyGroupFileBaseName(group)}.json`,
      }),
    ),
  );
}

function writeStudyGroups(groups: StudyGroup[]): void {
  const router = getStorageRouter();
  const ts = now();
  const newIds = new Set(groups.map((g) => g.id));

  for (const ref of listLocalRefs('study-groups')) {
    if (ref.deletedAt) continue;
    if (!newIds.has(ref.key)) void router.deleteRecord('study-groups', ref.key);
  }

  for (const group of groups) {
    void router.putRecord<StudyGroup>({
      namespace: 'study-groups',
      key: group.id,
      value: group,
      updatedAt: group.updatedAt ?? ts,
      displayName: group.name,
      recordFileName: `${studyGroupFileBaseName(group)}.json`,
    });
  }
}

// ── Chat ──────────────────────────────────────────────────────────

export function saveChatMessages<T>(messages: T[]): void {
  saveSingleton('chat', messages);
}
export function loadChatMessages<T>(): T[] {
  return loadSingleton<T[]>('chat') ?? [];
}

export function saveActiveConversation(id: string | null): void {
  const router = getStorageRouter();
  if (id === null) {
    void router.deleteRecord('prefs', 'active-conversation');
    return;
  }
  void router.putRecord<string>({
    namespace: 'prefs',
    key: 'active-conversation',
    value: id,
    updatedAt: now(),
  });
}

export function loadActiveConversation(): string | null {
  const rec = readLocalRecord<string>('prefs', 'active-conversation');
  if (!rec || rec.deletedAt) return null;
  return rec.value;
}

// ── Re-exports of value type (used internally) ────────────────────

export type { StorageRecord };
